import { useQuery } from '@tanstack/react-query'
import { axiosClient } from 'api/apiClient'
import { staleTime24Hours } from 'use-towns-client'
import { env } from 'utils'
import { Address, isAddress } from 'viem'

type OtherUsersAbstractAccountAddressResponse = [
    {
        address: Address
        accountType: 'simple' | 'modular'
        implementationAddress: Address
    },
]

/**
 * @param userId - The user's ROOT key address aka userId
 * @returns The user's abstract account address, if they have one
 */
export function useOtherUsersAbstractAccountAddress(userId: string | undefined) {
    const isUserAddress = userId && isAddress(userId)
    return useQuery({
        queryKey: ['otherUsersAbstractAccountAddress', userId],
        queryFn: async () => {
            if (!isUserAddress) {
                console.warn('[useOtherUsersAbstractAccountAddress] Invalid user address', userId)
                return
            }

            const url = new URL(env.VITE_AA_PAYMASTER_PROXY_URL)
            url.pathname = `/api/deployed-smart-accounts/${userId}`
            const response = await axiosClient.get(url.toString())
            return response.data.data
        },
        select: (data: OtherUsersAbstractAccountAddressResponse | undefined) => {
            if (!data) {
                return
            }

            // very rare for users to have > 1 deployed smart account, but if so, we only care about modular
            if (data.length > 1) {
                return data.filter((item) => item.accountType === 'modular')[0]?.address
            }

            return data[0]?.address
        },
        enabled: !!isUserAddress,
        staleTime: staleTime24Hours,
        gcTime: staleTime24Hours,
    })
}
